import { LucideIcon } from '@kerfjs/ui/lucide-icon';
import { PopupMenu, type PopupMenuEntry, type PopupMenuItem } from '@kerfjs/ui/popup-menu';
import { type SafeHtml } from 'kerfjs';
import { Copy, Download, Eye, Files, Library, ListChecks, ReceiptText, RefreshCw, Tags, Trash2 } from 'lucide';

import { libraryProjects, selectedDocumentIds } from '../../../data/state.js';
import { menuProps, type MenuTrigger } from '../../../lib/action-menu.js';
import { isImageDocument } from '../../../lib/entity-reference.js';
import { demandDocuments, documentType } from './document-helpers.js';

export function DocumentActionsMenu({ count, trigger }: { count: number; trigger: MenuTrigger }): SafeHtml {
  const selected = demandDocuments().filter((document) => selectedDocumentIds.value.includes(document.id));
  const one = selected.length === 1 ? selected[0] : undefined;
  const allType = new Set(selected.map((document) => documentType(document).target));
  const selectedType = allType.size === 1 ? [...allType][0] : undefined;
  const projectTargets = libraryProjects.value.filter((project) => project.status !== 'archived');
  const typeChoice = (label: string, icon: SafeHtml, id: string): PopupMenuItem => ({
    label,
    icon,
    action: 'documents-change-type',
    checked: selectedType === id,
    attributes: { 'data-item-id': id },
  });
  const entries: PopupMenuEntry[] = [
    {
      label: `Download ${String(count)} Selected`,
      icon: <LucideIcon icon={Download} name="" />,
      action: 'documents-download',
    },
    {
      label: 'Preview Image',
      icon: <LucideIcon icon={Eye} name="" />,
      action: 'document-preview',
      disabled: one === undefined || !isImageDocument(one),
    },
    {
      label: count === 1 ? 'Copy File Reference' : 'Copy File References',
      icon: <LucideIcon icon={Copy} name="" />,
      action: 'documents-copy-reference',
    },
    {
      label: 'Replace with New Version',
      icon: <LucideIcon icon={RefreshCw} name="" />,
      action: 'document-replace-open',
      disabled: one === undefined,
    },
    // The document types live in a Type submenu, like Decide on line items (HS2-P1CMEP).
    {
      label: 'Type',
      icon: <LucideIcon icon={Tags} name="" />,
      submenu: [
        typeChoice('Line Items', <LucideIcon icon={ListChecks} name="" />, 'demand'),
        typeChoice('Quotes', <LucideIcon icon={ReceiptText} name="" />, 'quotation'),
        typeChoice('Supplemental', <LucideIcon icon={Files} name="" />, 'supporting'),
      ],
    },
    ...(one?.kind === 'supporting'
      ? [
          {
            label: 'Promote to Organization Knowledge Base',
            icon: <LucideIcon icon={Library} name="" />,
            action: 'document-promote',
          },
          ...(projectTargets.length > 0
            ? [
                {
                  label: 'Promote to Project Knowledge Base',
                  icon: <LucideIcon icon={Library} name="" />,
                  submenu: projectTargets.map((project) => ({
                    label: project.name,
                    action: 'document-promote',
                    attributes: { 'data-project-id': project.id },
                  })),
                },
              ]
            : []),
        ]
      : []),
    { kind: 'divider' },
    {
      label: `Delete ${String(count)} Selected`,
      icon: <LucideIcon icon={Trash2} name="" />,
      action: 'documents-delete',
    },
    { label: 'Clear Selection', action: 'documents-clear-selection' },
  ];
  return <PopupMenu {...menuProps(trigger, entries)} />;
}
